import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const LoanManagement = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    loanAmount: '',
    documentCharges: '',
    numberOfInstallments: '',
    installmentAmount: '',
    firstInstallment: '',
    installmentIntervalDays: '5',
    bankDeposit: '',
    description: '',
    active: true,
  });
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/admin/loan-templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const calculateBankDeposit = () => {
    // If bank deposit is manually entered, use it; otherwise calculate
    if (formData.bankDeposit && formData.bankDeposit !== '') {
      return parseFloat(formData.bankDeposit) || 0;
    }
    const loanAmount = parseFloat(formData.loanAmount) || 0;
    const documentCharges = parseFloat(formData.documentCharges) || 0;
    const firstInstallment = parseFloat(formData.firstInstallment) || 0;
    return loanAmount - documentCharges - firstInstallment;
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.name || formData.name.trim() === '') {
      setMessage('Error: Template name is required');
      return;
    }

    const loanAmount = parseFloat(formData.loanAmount);
    const documentCharges = parseFloat(formData.documentCharges);
    const numberOfInstallments = parseInt(formData.numberOfInstallments);
    const installmentAmount = parseFloat(formData.installmentAmount);
    const firstInstallment = parseFloat(formData.firstInstallment);
    const installmentIntervalDays = parseInt(formData.installmentIntervalDays) || 5;

    if (isNaN(loanAmount) || loanAmount <= 0) {
      setMessage('Error: Please enter a valid loan amount (greater than 0)');
      return;
    }

    if (isNaN(documentCharges) || documentCharges < 0) {
      setMessage('Error: Please enter a valid document charges amount (0 or greater)');
      return;
    }

    if (isNaN(numberOfInstallments) || numberOfInstallments <= 0) {
      setMessage('Error: Please enter a valid number of installments (greater than 0)');
      return;
    }

    if (isNaN(installmentAmount) || installmentAmount <= 0) {
      setMessage('Error: Please enter a valid installment amount (greater than 0)');
      return;
    }

    if (isNaN(firstInstallment) || firstInstallment <= 0) {
      setMessage('Error: Please enter a valid first installment amount (greater than 0)');
      return;
    }

    if (installmentIntervalDays < 1) {
      setMessage('Error: Days between installments must be at least 1');
      return;
    }

    if (!user || !user.userId) {
      setMessage('Error: Admin user not found. Cannot save template.');
      return;
    }

    try {
      const bankDeposit = calculateBankDeposit();
      
      // Ensure bank deposit is valid
      if (isNaN(bankDeposit) || bankDeposit < 0) {
        setMessage('Error: Invalid bank deposit calculation. Please check your loan amount, document charges, and first installment.');
        return;
      }

      // Ensure installmentIntervalDays is always a valid number (at least 1)
      const finalInstallmentIntervalDays = (installmentIntervalDays && installmentIntervalDays >= 1) 
        ? installmentIntervalDays 
        : 5;

      const templateData = {
        name: formData.name.trim(),
        loanAmount: loanAmount,
        documentCharges: documentCharges,
        numberOfInstallments: numberOfInstallments,
        installmentAmount: installmentAmount,
        firstInstallment: firstInstallment,
        installmentIntervalDays: finalInstallmentIntervalDays,
        bankDeposit: bankDeposit,
        description: formData.description ? formData.description.trim() : null,
        active: formData.active !== undefined ? formData.active : true,
      };

      console.log('Sending template data:', templateData);
      console.log('Admin ID:', user.userId);

      if (editMode) {
        await api.put(`/admin/loan-templates/${formData.id}`, templateData);
        setMessage('Loan template updated successfully!');
      } else {
        const response = await api.post('/admin/loan-templates', templateData, {
          params: { adminId: user.userId },
        });
        console.log('Template created successfully:', response.data);
        setMessage('Loan template created successfully!');
      }
      fetchTemplates();
      setOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving template:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      
      let errorMessage = 'Failed to save template';
      
      if (error.response?.data) {
        // Check for error object
        if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else {
          // Try to extract any error message from the response
          errorMessage = JSON.stringify(error.response.data);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setMessage('Error: ' + errorMessage);
    }
  };

  const handleEdit = (template) => {
    setFormData({
      id: template.id,
      name: template.name,
      loanAmount: template.loanAmount,
      documentCharges: template.documentCharges,
      numberOfInstallments: template.numberOfInstallments,
      installmentAmount: template.installmentAmount,
      firstInstallment: template.firstInstallment,
      installmentIntervalDays: template.installmentIntervalDays || '5',
      bankDeposit: template.bankDeposit || '',
      description: template.description || '',
      active: template.active,
    });
    setEditMode(true);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this loan template?')) {
      try {
        await api.delete(`/admin/loan-templates/${id}`);
        fetchTemplates();
        setMessage('Loan template deleted successfully!');
      } catch (error) {
        setMessage('Error deleting template: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      loanAmount: '',
      documentCharges: '',
      numberOfInstallments: '',
      installmentAmount: '',
      firstInstallment: '',
      installmentIntervalDays: '5',
      bankDeposit: '',
      description: '',
      active: true,
    });
    setEditMode(false);
    setMessage('');
  };

  return (
    <Box>
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: 3,
          gap: 2,
        }}
      >
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700,
            color: '#0D47A1',
            fontSize: { xs: '1.75rem', sm: '2rem' },
          }}
        >
          Loan Management
        </Typography>
        <Button 
          variant="contained" 
          onClick={() => { resetForm(); setOpen(true); }}
          sx={{
            background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)',
            boxShadow: '0 4px 15px rgba(13, 71, 161, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #1565C0 0%, #0A3D91 100%)',
              boxShadow: '0 6px 20px rgba(13, 71, 161, 0.5)',
            },
            minWidth: { xs: '100%', sm: 'auto' },
          }}
        >
          Add Loan Template
        </Button>
      </Box>
      {message && (
        <Alert 
          severity={message.includes('Error') ? 'error' : 'success'} 
          sx={{ mb: 2, borderRadius: 2 }} 
          onClose={() => setMessage('')}
        >
          {message}
        </Alert>
      )}
      <TableContainer 
        component={Paper}
        sx={{
          boxShadow: '0 4px 20px rgba(13, 71, 161, 0.1)',
          borderRadius: 2,
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': {
            height: 8,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#0D47A1',
            borderRadius: 4,
          },
        }}
      >
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow sx={{ background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)' }}>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>Name</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>Loan Amount</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>Document Charges</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>First Installment</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>Installment Amount</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>No. of Installments</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>Bank Deposit</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>Status</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No loan templates found. Click "Add Loan Template" to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              templates.map((template) => (
                <TableRow 
                  key={template.id}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.05)',
                    },
                  }}
                >
                  <TableCell sx={{ fontWeight: 500, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>{template.name}</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>Rs {template.loanAmount?.toFixed(2)}</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>Rs {template.documentCharges?.toFixed(2)}</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>Rs {template.firstInstallment?.toFixed(2)}</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>Rs {template.installmentAmount?.toFixed(2)}</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>{template.numberOfInstallments}</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>Rs {template.bankDeposit?.toFixed(2)}</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>
                    <Chip 
                      label={template.active ? 'Active' : 'Inactive'} 
                      color={template.active ? 'success' : 'default'} 
                      size="small"
                      sx={{ fontWeight: 500, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton 
                        size="small" 
                        onClick={() => handleEdit(template)}
                        sx={{ color: '#0D47A1', padding: { xs: '4px', sm: '8px' } }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleDelete(template.id)}
                        sx={{ color: '#D32F2F', padding: { xs: '4px', sm: '8px' } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog 
        open={open} 
        onClose={() => { setOpen(false); resetForm(); }} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 2 },
            m: { xs: 0, sm: 2 },
            maxHeight: { xs: '100%', sm: '90vh' },
          },
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)',
            color: 'white',
            fontWeight: 600,
          }}
        >
          {editMode ? 'Edit' : 'Add'} Loan Template
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Template Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: '#0D47A1',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#0D47A1',
                },
              },
            }}
          />
          <TextField
            fullWidth
            label="Loan Amount"
            type="number"
            value={formData.loanAmount}
            onChange={(e) => setFormData({ ...formData, loanAmount: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Document Charges"
            type="number"
            value={formData.documentCharges}
            onChange={(e) => setFormData({ ...formData, documentCharges: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Number of Installments"
            type="number"
            value={formData.numberOfInstallments}
            onChange={(e) => setFormData({ ...formData, numberOfInstallments: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Days Between Each Installment"
            type="number"
            value={formData.installmentIntervalDays}
            onChange={(e) => setFormData({ ...formData, installmentIntervalDays: e.target.value })}
            margin="normal"
            required
            helperText="Number of days between each installment. The system will automatically generate installment dates based on this value."
            inputProps={{ min: 1 }}
          />
          <TextField
            fullWidth
            label="First Installment Amount"
            type="number"
            value={formData.firstInstallment}
            onChange={(e) => setFormData({ ...formData, firstInstallment: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Installment Amount (for remaining installments)"
            type="number"
            value={formData.installmentAmount}
            onChange={(e) => setFormData({ ...formData, installmentAmount: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Bank Deposit"
            type="number"
            value={formData.bankDeposit}
            onChange={(e) => setFormData({ ...formData, bankDeposit: e.target.value })}
            margin="normal"
            helperText="Enter the bank deposit amount. If left empty, it will be calculated as: Loan Amount - Document Charges - First Installment"
          />
          {calculateBankDeposit() > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {formData.bankDeposit && formData.bankDeposit !== '' 
                ? `Bank Deposit: Rs ${calculateBankDeposit().toFixed(2)} (Manually set)`
                : `Calculated Bank Deposit: Rs ${calculateBankDeposit().toFixed(2)}`}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => { setOpen(false); resetForm(); }}
            sx={{ color: '#0D47A1' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1565C0 0%, #0A3D91 100%)',
              },
            }}
          >
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LoanManagement;


